<!-- A widget to start/stop a dialog tv spot preview -->

<template>
  <!-- FIXME:

  Please be aware that an inline player (as we have used
  before) is a problem when used together with a @click handler on
  a wrapping v-list-tile, which will make the whole tile
  clickable: Both click events will be handled and we can't use
  @click.native.stop here for some reason to prevent that. Until a
  solution is found, this should be avoided. For that reason we
  are using an extra dialog as the default preview player.

  Test this widget in Chrome and Firefox, as in Firefox there are some
  subtle differences in layouting and how width/height are populated by
  the mejs video player that required tweaking.

  -->
  <div
    class="firefox-stacking-context-fix"
    @click.prevent.stop="showPlayer = true"
  >
    <!-- Image Poster while playback not started -->
    <div
      v-if="useInlineVideoPlayer ? !showPlayer || useIframePreview : true"
      class="poster-wrapper"
      :class="{
        small: small,
        medium: medium,
        full: full,
        portrait: usePortraitAdaptation,
      }"
    >
      <!-- Placeholder while poster image is loaded. -->
      <v-progress-circular
        v-if="!imageLoaded"
        :size="20"
        :width="5"
        indeterminate
        class="grey--text text--lighten-4 image-placeholder"
      />
      <img
        :src="imageURL"
        class="poster"
        :class="{
          small: small,
          medium: medium,
          full: full,
          portrait: usePortraitAdaptation,
        }"
        @load="() => (imageLoaded = true)"
      >
    </div>
    <img
      v-if="useInlineVideoPlayer ? !showPlayer || useIframePreview : true"
      src="../../static/btn_play.png"
      class="play-btn-overlay"
      :class="{
        small: small,
        medium: medium,
        full: full,
        portrait: isPortraitFormat,
      }"
    >
    <div
      v-if="useInlineVideoPlayer && !useIframePreview"
      :style="{
        width: dialogDimensions.width + 'px',
        'max-width': dialogDimensions.width + 'px',
        'max-height': dialogDimensions.height + 'px',
      }"
    >
      <mini-preview-video-player
        v-if="showPlayer"
        :video-url="previewUrl"
        :poster-url="posterUrl"
        :width="dialogDimensions.width"
        :height="dialogDimensions.height"
      />
    </div>
    <div v-else>
      <mini-preview-html-dialog
        v-if="useIframePreview"
        :show.sync="showPlayer"
        :dialog-width="dialogDimensions.width"
        :dialog-height="dialogDimensions.height"
        :spot="spot"
        :tvformat="tvformat"
      />
      <mini-preview-video-dialog
        v-else
        :show.sync="showPlayer"
        :dialog-width="dialogDimensions.width"
        :dialog-height="dialogDimensions.height"
        :preview-url="previewUrl"
        :poster-url="posterUrl"
      />
    </div>
  </div>
</template>

<script>
import MiniPreviewHtmlDialog from '@/components/MiniPreviewHtmlDialog';
import MiniPreviewVideoDialog from '@/components/MiniPreviewVideoDialog';
import MiniPreviewVideoPlayer from '@/components/MiniPreviewVideoPlayer';

export default {
  components: {
    MiniPreviewHtmlDialog,
    MiniPreviewVideoDialog,
    MiniPreviewVideoPlayer,
  },
  props: {
    spot: {
      type: Object,
      required: true,
    },
    tvformat: {
      type: Object,
      required: true,
    },
    /* Whether to play video inline instead of using a separate dialog. */
    useInlineVideoPlayer: {
      type: Boolean,
      default: false,
    },
    allowHtmlPreview: {
      type: Boolean,
      default: false,
    },
    allowPortraitAdaptation: {
      type: Boolean,
      default: false,
    },
    /** Default preview is tiny. We can increase it. */
    small: {
      type: Boolean,
      default: false,
    },
    medium: {
      type: Boolean,
      default: false,
    },
    full: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPlayer: false,
      imageLoaded: false,
      player: null,
    };
  },
  computed: {
    useIframePreview() {
      return this.allowHtmlPreview && !!this.spot.editable_template;
    },
    previewUrl() {
      return this.spot.preview_url;
    },
    thumbnailUrl() {
      return this.spot.thumbnail_url;
    },
    posterUrl() {
      return this.spot.poster_url;
    },
    imageURL() {
      if (this.small || this.medium || this.full) {
        return this.posterUrl;
      }
      return this.thumbnailUrl;
    },
    aspectRatio() {
      return this.spot.width / this.spot.height;
    },
    isPortraitFormat() {
      return this.aspectRatio < 1;
    },
    usePortraitAdaptation() {
      if (!this.allowPortraitAdaptation) {
        return false;
      }
      return this.isPortraitFormat;
    },
    /**
     * Width is limited by device size up to a fixed value.
     */
    maxDialogWidth() {
      const max = this.$vuetify.breakpoint.xsOnly ? 400 : 1024;
      if (this.$vuetify.breakpoint.width < max) {
        return this.$vuetify.breakpoint.width;
      }
      return max;
    },
    /**
     * Height is limited by device size up to a fixed value.
     */
    maxDialogHeight() {
      const max = this.$vuetify.breakpoint.xsOnly ? 400 : 800;
      if (this.$vuetify.breakpoint.height < max) {
        return this.$vuetify.breakpoint.height;
      }
      return max;
    },
    // TODO: See if we can merge this with SpotDetail -> tvPreviewMaxDimensions()
    dialogDimensions() {
      const fixedMaxWidth = this.maxDialogWidth;
      const fixedMaxHeight = this.maxDialogHeight;

      const isStretch =
        this.spot.width === 1920 &&
        this.spot.height === 540 &&
        !this.spot.is_multiscreen;
      const isPortrait =
        this.spot.height > this.spot.width && !this.spot.is_multiscreen;
      const isLandscape = !isPortrait;

      let calculatedWidth, calculatedHeight;

      if (isStretch) {
        calculatedWidth = fixedMaxWidth;
        calculatedHeight = (calculatedWidth / 32.0) * 9.0;
      } else if (isPortrait) {
        calculatedHeight = fixedMaxHeight;
        calculatedWidth = (calculatedHeight / 16.0) * 9.0;
      } else if (isLandscape) {
        const marginRight = 48 + 16;
        calculatedWidth = this.$vuetify.breakpoint.width - marginRight;
        if (calculatedWidth > fixedMaxWidth) {
          calculatedWidth = fixedMaxWidth;
        }
        calculatedHeight = (calculatedWidth / 16.0) * 9.0;
      }

      return {width: calculatedWidth, height: calculatedHeight};
    },
  },
};
</script>

<style scoped>
.poster-wrapper {
  width: 96px;
  height: 54px;
  display: flex;
  justify-items: center;
}
.poster-wrapper.portrait {
  width: 54px;
  height: 96px;
}
.poster-wrapper.small {
  width: calc(96px * 2);
  height: calc(54px * 2);
}
.poster-wrapper.small.portrait {
  width: calc(54px * 2);
  height: calc(96px * 2);
}
.poster-wrapper.medium {
  width: calc(96px * 3);
  height: calc(54px * 3);
}
.poster-wrapper.medium.portrait {
  width: calc(54px * 3);
  height: calc(96px * 3);
}
.poster-wrapper.full {
  width: 100%;
  height: auto;
}

.poster {
  max-width: 96px;
  max-height: 54px;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.poster.portrait {
  max-width: 54px;
  max-height: 96px;
}
.poster.small {
  max-width: calc(96px * 2);
  max-height: calc(54px * 2);
}
.poster.small.portrait {
  max-width: calc(54px * 2);
  max-height: calc(96px * 2);
}
.poster.medium {
  max-width: calc(96px * 3);
  max-height: calc(54px * 3);
}
.poster.medium.portrait {
  max-width: calc(54px * 3);
  max-height: calc(96px * 3);
}
.poster.full {
  max-width: initial;
  max-height: 400px;
}

.play-btn-overlay {
  transform: scale(0.85);
  position: absolute;
  top: 0px;
  max-width: 96px;
  max-height: 54px;
}
.play-btn-overlay.portrait {
  transform: scale(0.7);
}
.play-btn-overlay.small {
  left: calc(50% - 96px);
  top: calc(50% - 54px);
  max-width: calc(96px * 2);
  max-height: calc(54px * 2);
}
.play-btn-overlay.medium {
  left: calc(50% - 96px);
  top: calc(50% - 54px);
  max-width: calc(96px * 3);
  max-height: calc(54px * 3);
}
.play-btn-overlay.full {
  left: calc(50% - 96px);
  top: calc(50% - 54px);
  width: 192px;
  height: 108px;
  max-width: 192px;
  max-height: 108px;
}

.image-placeholder {
  position: absolute;
  left: 38px;
  top: 17px;
}
</style>



// WEBPACK FOOTER //
// src/components/MiniPreviewVideo.vue